/**
 * tutorApplicationViewModel — la page de candidature (l'onglet `dashboard` du candidat), lue sur le
 * compte (`state.data.currentUser.object`, relu par `getUser` à chaque retour sur l'écran).
 *
 * Patron A : le compte est dans le store. Rien n'est dessiné tant que les étapes ne sont pas là (le
 * squelette, l'ancien n'affichait rien) ; pendant une relecture, le bouton de l'étape tourne (l'ancien
 * « GO ! » tournait sur `getUser`).
 */
import { createSelector } from "reselect"

import {
    type ApiTutorCandidate,
    applicationStepDestination,
    currentApplicationStep,
    isVideoApplicationStep,
    toUITutorApplication,
} from "../../../adapters/tutor/Application/tutorApplicationAdapter"
import { type UITutorApplication } from "../../../ui/types/tutorCandidature"
import { selectAccount } from "../../shared/displayedStudent"

type State = Parameters<typeof selectAccount>[0] & { api?: { getUser?: { request?: boolean } } }

export type TutorApplicationViewModel = {
    application: UITutorApplication | null
    loading: boolean
    refreshing: boolean
    /** L'étape du moment (1 à 7), 0 sans compte. */
    currentStep: number
    /** L'étape du moment se répond en vidéo. */
    currentStepIsVideo: boolean
    destination: ReturnType<typeof applicationStepDestination>
}

export const tutorApplicationViewModel = createSelector(
    [
        (state: State) => selectAccount(state) as ApiTutorCandidate | null,
        (state: State) => !!state?.api?.getUser?.request,
    ],
    (user, refreshing): TutorApplicationViewModel => {
        const application = toUITutorApplication(user)
        const { step } = currentApplicationStep(user)
        return {
            application,
            loading: application == null,
            refreshing,
            currentStep: step,
            currentStepIsVideo: isVideoApplicationStep(user?.application_steps?.[step - 1]),
            destination: applicationStepDestination(user),
        }
    }
)
